import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'hooks/store';
import {
  cleanOrdersAction,
  getOrderListAction,
  isLoadingOrdersSelector,
  ordersEntitiesSelector,
  ordersIdsSelector,
  remoteTotalSelector,
  setOrderStatusAction,
} from 'state/slices/order';
import { usePagination } from 'hooks/pagination';
import {
  Client,
  ClientLocation,
  Order,
  Organization,
} from 'constants/interface';
import { Search, Table, Tag } from 'UI';
import {
  OrderViewStatusEnum,
  OrderStatus,
  OrderStatusDisplayMapping,
  OrderTypeDisplayMapping,
  OrderTypeEnum,
  OrderProposalStatusEnum,
  OrderApiStatus,
  OrderProductionStatusEnum,
} from 'constants/order';
import { GetOrderList } from 'api/order';
import { createAlertNotification } from 'infra/notification';
import { AlertNotificationType, setNotificationAction } from 'state/slices/notification';

import TableRowAction, { ActionTypeEnum } from './components/TableRowAction';

import {
  SearchContainer,
} from './style';

interface ProposalsTabProps {
  onOrderCopy: (orderId: string) => void
  selectedOrg: Organization
}

const ProposalsTab = ({
  onOrderCopy,
  selectedOrg,
}: ProposalsTabProps): JSX.Element => {
  const dispatch = useDispatch();
  const remoteTotal = useSelector(remoteTotalSelector);

  const ordersEntities = useSelector(ordersEntitiesSelector);
  const ordersIds = useSelector(ordersIdsSelector);
  const isRemoteLoading = useSelector(isLoadingOrdersSelector);

  const [isLoading, setLoading] = useState(false);

  const orders = ordersIds.map((orderId) => {
    const entity = ordersEntities[orderId];
    if (!entity) return undefined;
    const tableOrder: Order & {
      key?: string,
    } = {
      ...entity,
      key: entity.id,
    };
    return tableOrder;
  }).filter((tableOrder) => !!tableOrder) as (Order & { key: string})[];

  const [currentActiveAction, setCurrentActiveAction] = useState<number>();

  const remoteFetch = (
    paginationData: GetOrderList,
  ) => {
    const filterStatus = paginationData.filter?.status as OrderApiStatus | undefined;
    const finalStatus = filterStatus === undefined
      ? OrderViewStatusEnum.PROPOSAL
      : filterStatus;

    dispatch(getOrderListAction({
      ...paginationData,
      filter: {
        ...paginationData.filter,
        status: finalStatus,
      },
      order: paginationData.order || 'descend',
    }));
  };
  const pagination = usePagination<Order>(
    remoteFetch,
    {
      filter: {
        // status: OrderViewStatusEnum.PROPOSAL,
        organization: selectedOrg.id,
      },
      order: 'descend',
    },
  );

  useEffect(() => {
    dispatch(cleanOrdersAction());
  }, []);

  useEffect(() => {
    pagination.updateTotal(remoteTotal);
  }, [remoteTotal]);

  const onSendOrderToProduction = async (orderId: string) => {
    try {
      setLoading(true);
      await dispatch(setOrderStatusAction({
        orderId,
        status: OrderProductionStatusEnum.PRODUCTION,
      }));
      setLoading(false);
      pagination.fetch();
    } catch (err) {
      const errorAlert = createAlertNotification(
        'Erro interno ao enviar pedido para semeio. Tente novamente.',
        AlertNotificationType.Error,
      );
      dispatch(setNotificationAction(errorAlert));
      setLoading(false);
    }
  };

  const onAction = (orderId: string, actionType: ActionTypeEnum) => {
    const actionMapper: Record<ActionTypeEnum, (orderId: string) => void> = {
      [ActionTypeEnum.COPY]: onOrderCopy,
      [ActionTypeEnum.SEND_TO_PRODUCTION]: onSendOrderToProduction,
    };

    const action = actionMapper[actionType];
    action(orderId);
  };

  const sorter = (): number => 0;

  const pageData = pagination.getInternal();

  const orderTypeFilter = pageData.filter.type ?? null;
  const orderStatusFilter = pageData.filter.status ?? null;

  return (
    <>
      <SearchContainer>
        <Search placeholder="Buscar propostas" onSearch={pagination.onSearch} allowClear />
      </SearchContainer>
      <Table<Order>
        dataSource={orders}
        pagination={pageData}
        loading={isRemoteLoading || isLoading}
        showSorterTooltip={false}
        locale={{
          filterReset: 'Limpar',
          cancelSort: 'Clique para limpar ordenação',
          triggerAsc: 'Clique para ordem crescente',
          triggerDesc: 'Clique para ordem decrescente',
        }}
        onChange={pagination.onTableChange}
      >
        <Table.Column
          title="OS​"
          key="seq"
          dataIndex="seq"
        />
        <Table.Column
          title="Cliente"
          key="client"
          dataIndex="client"
          render={(
            client: Client,
          ) => client.name}
        />
        <Table.Column
          title="Localização"
          key="location"
          dataIndex="location"
          render={(
            location: ClientLocation,
            order: Order,
          ) => {
            const display = location
              ? `${location.name} - ${location.city}/${location.state} (${location.maintainer})`
              : `${order.client.city}/${order.client.state}`;
            return display;
          }}
        />
        <Table.Column
          title="Formação​"
          key="type"
          dataIndex="type"
          filters={Object.values(OrderTypeEnum).map((orderType) => ({
            text: OrderTypeDisplayMapping[orderType].name,
            value: orderType,
          }))}
          filterMultiple={false}
          filteredValue={orderTypeFilter !== null ? [orderTypeFilter] : null}
          render={(type: OrderTypeEnum) => {
            const displayName = OrderTypeDisplayMapping[type].name;
            return displayName;
          }}
        />
        <Table.Column
          title="Data do pedido"
          key="createdAt"
          dataIndex="createdAt"
          sorter={sorter}
          defaultSortOrder="descend"
          sortOrder={
            pageData.orderBy === 'createdAt'
              ? pageData.order
              : undefined
          }
          render={(createdAt: string) => {
            const date = new Date(createdAt);
            const formattedDate = date.toLocaleDateString('pt-br');
            return formattedDate;
          }}
        />
        <Table.Column
          title="Status"
          key="status"
          dataIndex="status"
          filters={Object.values(OrderProposalStatusEnum).map((statusType) => ({
            text: OrderStatusDisplayMapping[statusType].name,
            value: statusType,
          }))}
          filterMultiple={false}
          filteredValue={orderStatusFilter !== null ? [orderStatusFilter] : null}
          render={(status: OrderStatus) => {
            const displayName = OrderStatusDisplayMapping[status].name;
            return (
              <Tag color={OrderStatusDisplayMapping[status].color}>
                {displayName}
              </Tag>
            );
          }}
        />
        <Table.Column
          title=""
          key="action"
          dataIndex="id"
          render={(id: string, record: Order, index: number) => {
            const actionButton = (
              <TableRowAction
                order={record}
                index={index}
                isActive={currentActiveAction === index}
                onActionSelected={onAction}
                onVisibleChange={(visibleIndex) => setCurrentActiveAction(visibleIndex)}
              />
            );
            return actionButton;
          }}
        />
      </Table>
    </>
  );
};

export default ProposalsTab;
